import React, { useEffect, useMemo, useState } from "react";
import "./TreeView.scss";

const getFileIcon = (filename = "") => {
  const ext = filename.split(".").pop().toLowerCase();
  switch (ext) {
    case "pdf":
      return "fas fa-file-pdf text-danger";
    case "doc":
    case "docx":
      return "fas fa-file-word text-primary";
    case "xls":
    case "xlsx":
      return "fas fa-file-excel text-success";
    case "ppt":
    case "pptx":
      return "fas fa-file-powerpoint text-warning";
    case "txt":
      return "fas fa-file-alt text-secondary";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "fas fa-file-image text-info";
    default:
      return "fas fa-file";
  }
};

const CheckboxTreeView = ({
  data = [],
  checkedIds = [],
  onChange = () => {},
  checkboxType = "file", // "file" | "folder" | "all"
  disabled = false,
  defaultExpanded = true,
  onFolderToggle = () => {},
  accordionSiblings = false,
  isActive = true,
  cascadeFolders = true,
  showIndeterminate = false,
}) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [localCheckedIds, setLocalCheckedIds] = useState(new Set(checkedIds));

  useEffect(() => {
    setLocalCheckedIds(new Set(checkedIds));
  }, [checkedIds]);

  // When `isActive` is false, collapse everything (used for external-studies accordion).
  useEffect(() => {
    if (!isActive) setExpandedIds(new Set());
  }, [isActive]);

  const allFolderIds = useMemo(() => {
    const ids = [];
    const walk = (nodes) => {
      (nodes || []).forEach((n) => {
        if (n?.type === "folder") ids.push(n.id);
        if (n?.children?.length) walk(n.children);
      });
    };
    walk(data);
    return ids;
  }, [data]);

  const nodeMetaById = useMemo(() => {
    const map = new Map();
    const walk = (nodes, parentId = null) => {
      (nodes || []).forEach((n) => {
        const currentParentId =
          n?.parentId !== undefined ? n.parentId : parentId;
        map.set(n.id, {
          type: n?.type,
          parentId: currentParentId,
        });
        walk(n?.children || [], currentParentId);
      });
    };
    walk(data, null);
    return map;
  }, [data]);

  useEffect(() => {
    if (!defaultExpanded) return;
    setExpandedIds(new Set(allFolderIds));
  }, [allFolderIds, defaultExpanded]);

  const toggleExpanded = (node) => {
    const id = node?.id;
    if (!id) return;

    setExpandedIds((prev) => {
      const next = new Set(prev);

      const isExpanded = next.has(id);
      if (isExpanded) {
        next.delete(id);
        return next;
      }

      // Accordion behavior: when expanding a folder, collapse all other expanded
      // sibling folders (same parentId).
      if (accordionSiblings && node?.type === "folder") {
        const meta = nodeMetaById.get(id);
        const parentId = meta?.parentId ?? null;

        for (const expandedId of Array.from(next)) {
          const expandedMeta = nodeMetaById.get(expandedId);
          if (!expandedMeta) continue;
          const sameParent = (expandedMeta.parentId ?? null) === parentId;
          if (sameParent && expandedMeta.type === "folder") next.delete(expandedId);
        }
      }

      next.add(id);
      return next;
    });
  };

  const collectDescendantIds = (node) => {
    const ids = [];
    const walk = (n) => {
      (n?.children || []).forEach((c) => {
        ids.push(c.id);
        if (c.children?.length) walk(c);
      });
    };
    walk(node);
    return ids;
  };

  const shouldShowCheckbox = (node) => {
    if (!node) return false;
    if (checkboxType === "all") return true;
    if (checkboxType === "folder") return node.type === "folder";
    if (checkboxType === "file") return node.type === "file";
    return false;
  };

  const isNodeDisabled = (node) => {
    if (disabled) return true;
    if (node?.isRestricted) return true;
    return false;
  };

  const applyCheckCascade = (node, checked) => {
    const next = new Set(localCheckedIds);

    // Cascade checks for folder nodes so selecting a folder selects its files.
    if (
      cascadeFolders &&
      (checkboxType === "folder" || checkboxType === "all") &&
      node?.type === "folder"
    ) {
      const toAffect = [node.id, ...collectDescendantIds(node)];
      toAffect.forEach((id) => {
        const nodeObj = findNodeById(data, id);
        if (!nodeObj) return;
        if (!shouldShowCheckbox(nodeObj)) return;
        if (isNodeDisabled(nodeObj)) return;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    }

    // Default: toggle only this node (used for file checkboxes).
    if (isNodeDisabled(node)) return next;
    if (checked) next.add(node.id);
    else next.delete(node.id);

    // Upward sync: if any descendant is checked, mark ancestors as checked.
    // If no descendants are checked, uncheck ancestors.
    const syncAncestors = (startingId) => {
      // Traverse parent chain using `parentId` metadata.
      let currentId = startingId;
      while (currentId) {
        const meta = nodeMetaById.get(currentId);
        const parentId = meta?.parentId;
        if (!parentId) break;

        const parentNodeObj = findNodeById(data, parentId);
        if (!parentNodeObj) {
          currentId = parentId;
          continue;
        }
        if (!shouldShowCheckbox(parentNodeObj)) {
          currentId = parentId;
          continue;
        }

        const descendantIds = collectDescendantIds(parentNodeObj);
        const anyDescChecked = descendantIds.some((id) => next.has(id));

        // If folder itself is restricted/disabled, keep it unchecked.
        if (isNodeDisabled(parentNodeObj)) {
          next.delete(parentId);
        } else if (anyDescChecked) {
          next.add(parentId);
        } else {
          next.delete(parentId);
        }

        currentId = parentId;
      }
    };

    syncAncestors(node?.id);
    return next;
  };

  const findNodeById = (nodes, id) => {
    for (const n of nodes || []) {
      if (n?.id === id) return n;
      if (n?.children?.length) {
        const found = findNodeById(n.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const emitChange = (nextSet) => {
    setLocalCheckedIds(nextSet);
    onChange(Array.from(nextSet));
  };

  const renderNodes = (nodes) => {
    return (nodes || []).map((node) => {
      const hasChildren = Boolean(node?.children?.length) || Boolean(node?.lazy);
      const expanded = expandedIds.has(node.id);
      const showCheckbox = shouldShowCheckbox(node);
      const isChecked = localCheckedIds.has(node.id);

      let isIndeterminate = false;
      if (showIndeterminate && node?.type === "folder" && !isChecked) {
        const descendantIds = collectDescendantIds(node);
        if (descendantIds?.length) {
          const anyDescChecked = descendantIds.some((id) =>
            localCheckedIds.has(id),
          );
          const allDescChecked = descendantIds.every((id) =>
            localCheckedIds.has(id),
          );
          isIndeterminate = anyDescChecked && !allDescChecked;
        }
      }

      return (
        <div key={node.id} className="tree-node">
          <div
            className={`node-content d-flex align-items-center ${
              expanded ? "" : ""
            }`}
          >
            {hasChildren ? (
              <span
                className="toggle-icon"
                onClick={() => {
                  const willExpand = !expandedIds.has(node.id);
                  toggleExpanded(node);
                  onFolderToggle?.(node, willExpand);
                }}
                role="button"
                aria-label={expanded ? "Collapse" : "Expand"}
                title={expanded ? "Collapse" : "Expand"}
              >
                {expanded ? "−" : "+"}
              </span>
            ) : (
              <span className="toggle-icon" style={{ opacity: 0 }} />
            )}

            {showCheckbox ? (
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isNodeDisabled(node)}
                ref={(el) => {
                  if (el) el.indeterminate = isIndeterminate;
                }}
                onChange={(e) => {
                  const next = applyCheckCascade(node, e.target.checked);
                  emitChange(next);
                }}
              />
            ) : node.type === "file" ? (
              <i className={`me-2 ${getFileIcon(node.name || "")}`} />
            ) : null}

            {node.type === "folder" ? (
              <i className="fa fa-folder me-2 text-primary" />
            ) : null}

            <span className="node-label" title={node.name}>
              {node.name}
            </span>
          </div>

          {hasChildren && expanded && (
            <div className="tree-children">{renderNodes(node.children)}</div>
          )}
        </div>
      );
    });
  };

  return <div className="tree-view checkbox-tree-view">{renderNodes(data)}</div>;
};

export default CheckboxTreeView;

